import type {
  BlendPointRef,
  ResolvedTopologyPick,
  RuntimeTopologyHint,
  RuntimeTopologyFaceHint,
  SelectionItemRef,
  SerializedTopologyProvenanceMap,
} from '@puppycad/cad-core';
import {
  buildTopologyEdgeRef,
  buildTopologyFaceRef,
  buildTopologyVertexRef,
  FeatureFace,
  FeatureLine,
  FeaturePoint,
  FeatureResultMesh,
  isObjectInsideFeaturePreviewMesh,
  isFeatureFace,
  isFeatureLine,
  isFeaturePoint,
  isSketchPointObject,
  resolveTopologySelectionPick,
  resolveOwningFeatureResultMesh,
  type SketchPointObject,
} from '@puppycad/cad-core';
import type { BlendSelectionDisplayItem } from './types';

/**
 * 圆角/倒角选择对象。
 *
 * 设计说明：
 * 1. 当前分支的圆角/倒角选择已经不再依赖 web-client 自己拼装的 `SelectionEntity`；
 * 2. 真正的权威选择来源是 cad-core 暴露的拓扑代理对象；
 * 3. 因此本文件负责把拓扑代理对象投影为命令可消费的稳定引用。
 */
export type BlendSelectionObject = FeatureFace | FeatureLine | FeaturePoint;

type BlendSelectionBuildResult = {
  itemRef: SelectionItemRef;
  displayItem: BlendSelectionDisplayItem;
};

/**
 * 将 cad-core 选择层给出的权威拓扑命中快照投影为圆角/倒角宿主可消费的选择结果。
 *
 * 设计说明：
 * 1. `ResolvedTopologyPick` 已经在 cad-core 底层完成了命中收口，web-client 不应再次从 `Object3D` 反推引用；
 * 2. 该函数只负责把统一拓扑真相映射成 blend 宿主的 `SelectionItemRef + DisplayItem`；
 * 3. 这样 `SelectionUpdated`、属性栏列表、preview 请求和提交请求都能共享同一份稳定引用。
 *
 * @param pick 权威拓扑命中快照。
 * @returns blend 选择结果；当前命中不是 blend 可消费对象时返回 `null`。
 */
export function buildBlendSelectionFromTopologyPick(
  pick: ResolvedTopologyPick | null | undefined,
): BlendSelectionBuildResult | null {
  if (!pick) {
    return null;
  }

  const runtimeTopologyHint = pick.runtimeTopologyHint;
  if (pick.kind === 'edge') {
    return {
      itemRef: {
        kind: 'edge',
        bodyObjectId: pick.bodyObjectId,
        subEntityId: pick.subEntityId,
        semanticName: pick.semanticName,
        persistentRef: pick.persistentRef,
        owningFeatureId: pick.owningFeatureId,
        adjacentFaceRefs: pick.adjacentFaceRefs,
        runtimeTopologyHint,
      },
      displayItem: {
        key: `edge:${pick.persistentRef}`,
        label: '',
        kind: 'edge',
        objectId: pick.bodyObjectId,
        bodyObjectId: pick.bodyObjectId,
        persistentRef: pick.persistentRef,
        edgeIndex: pick.topologyIndex,
        subEntityId: pick.subEntityId,
        adjacentFaceRefs: pick.adjacentFaceRefs,
        runtimeTopologyHint,
      },
    };
  }

  if (pick.kind === 'face') {
    return {
      itemRef: {
        kind: 'face',
        bodyObjectId: pick.bodyObjectId,
        subEntityId: pick.subEntityId,
        persistentRef: pick.persistentRef,
        owningFeatureId: pick.owningFeatureId,
        runtimeTopologyHint,
      },
      displayItem: {
        key: `face:${pick.persistentRef}`,
        label: '',
        kind: 'face',
        objectId: pick.bodyObjectId,
        bodyObjectId: pick.bodyObjectId,
        persistentRef: pick.persistentRef,
        faceIndex: pick.topologyIndex,
        subEntityId: pick.subEntityId,
        runtimeTopologyHint,
      },
    };
  }

  if (pick.kind === 'vertex') {
    return {
      itemRef: {
        kind: 'vertex',
        bodyObjectId: pick.bodyObjectId,
        subEntityId: pick.subEntityId,
        persistentRef: pick.persistentRef,
        owningFeatureId: pick.owningFeatureId,
        runtimeTopologyHint,
      },
      displayItem: {
        key: `vertex:${pick.persistentRef}`,
        label: '',
        kind: 'vertex',
        objectId: pick.bodyObjectId,
        bodyObjectId: pick.bodyObjectId,
        persistentRef: pick.persistentRef,
        vertexIndex: pick.topologyIndex,
        subEntityId: pick.subEntityId,
        runtimeTopologyHint,
      },
    };
  }

  return null;
}

/**
 * 解析特征元数据。
 *
 * @param object 拓扑代理对象或结果体。
 * @returns 元数据快照。
 */
function resolveFeatureMetadata(
  object: BlendSelectionObject | FeatureResultMesh,
): Record<string, unknown> {
  if (object instanceof FeatureFace) {
    return object.getFeatureMetadata();
  }
  if (object instanceof FeatureLine) {
    return object.getFeatureMetadata();
  }
  if (object instanceof FeaturePoint) {
    return object.getFeatureMetadata();
  }
  return object.getFeatureMetadata();
}

/**
 * 解析拓扑出处映射。
 *
 * @param metadata 特征元数据。
 * @returns 可用的出处映射；缺失时返回 `null`。
 */
function resolveTopologyProvenance(
  metadata: Record<string, unknown>,
): SerializedTopologyProvenanceMap | null {
  const candidate = metadata.topologyProvenance as SerializedTopologyProvenanceMap | undefined;
  if (!candidate || !Array.isArray(candidate.faces) || !Array.isArray(candidate.edges)) {
    return null;
  }
  return candidate;
}

/**
 * 读取所属特征 ID。
 *
 * @param metadata 特征元数据。
 * @returns 所属特征 ID。
 */
function resolveOwningFeatureId(metadata: Record<string, unknown>): string | undefined {
  return typeof metadata.featureId === 'string' && metadata.featureId.length > 0
    ? metadata.featureId
    : undefined;
}

/**
 * 构建显示标签。
 *
 * @param kind 选择类型。
 * @param index 人类可读序号。
 * @returns 显示标签。
 */
function buildSelectionLabel(
  kind: BlendSelectionDisplayItem['kind'],
  index: number,
): string {
  switch (kind) {
    case 'edge':
      return `边线<${index}>`;
    case 'face':
      return `面<${index}>`;
    case 'vertex':
      return `顶点<${index}>`;
    default:
      return `项目<${index}>`;
  }
}

/**
 * 将快照中的局部顶点坐标转成序列化边/点引用所需的三元组。
 *
 * @param point 局部顶点坐标。
 * @returns 三维点元组。
 */
function toPointTuple(point: { x: number; y: number; z: number } | [number, number, number]): [number, number, number] {
  if (Array.isArray(point)) {
    return [point[0], point[1], point[2]];
  }
  return [point.x, point.y, point.z];
}

/**
 * 判断对象是否携带可用的世界点位。
 *
 * @param point 候选点位。
 * @returns 点位是否完整且有限。
 */
function isFinitePointLike(point: unknown): point is { x: number; y: number; z: number } {
  if (!point || typeof point !== 'object') {
    return false;
  }
  const candidate = point as { x?: unknown; y?: unknown; z?: unknown };
  return Number.isFinite(candidate.x)
    && Number.isFinite(candidate.y)
    && Number.isFinite(candidate.z);
}

/**
 * 解析草图点所属草图组 ID。
 *
 * 设计说明：参考等距允许吸收其它草图中的点，但求解阶段只需要稳定点坐标。
 * 这里仍保留所属草图组作为身份前缀，避免不同草图内同 UUID 或同坐标点在 UI 状态中互相覆盖。
 *
 * @param pointObject 草图点对象。
 * @returns 所属草图组 ID；找不到草图组时回退到点自身 ID。
 */
function resolveSketchPointOwnerId(pointObject: SketchPointObject): string {
  let current: unknown = pointObject.parent;
  while (current && typeof current === 'object') {
    if (
      Reflect.get(current, 'isSketchGroup') === true
      && typeof Reflect.get(current, 'uuid') === 'string'
    ) {
      return Reflect.get(current, 'uuid') as string;
    }
    current = Reflect.get(current, 'parent');
  }
  return pointObject.uuid;
}

/**
 * 将草图点转换为部分边线参考点。
 *
 * @param pointObject 草图点对象。
 * @returns 可写入部分边线参数的参考点。
 */
function buildBlendPointRefFromSketchPoint(pointObject: SketchPointObject): BlendPointRef | null {
  const point = pointObject.getPosition();
  if (!isFinitePointLike(point)) {
    return null;
  }
  const persistentRef = pointObject.uuid;
  return {
    bodyObjectId: resolveSketchPointOwnerId(pointObject),
    subEntityId: persistentRef,
    point: toPointTuple(point),
    persistentRef,
    displayLabel: pointObject.name ? `点<${pointObject.name}>` : `点<${persistentRef}>`,
  };
}

/**
 * 解析结果体上的运行时源对象。
 *
 * @param object 拓扑代理对象。
 * @returns 所属结果体；不存在时返回 `null`。
 */
export function resolveBlendSourceMesh(object: unknown): FeatureResultMesh | null {
  if (!object || typeof object !== 'object') {
    return null;
  }
  return resolveOwningFeatureResultMesh(object as FeatureFace | FeatureLine | FeaturePoint | FeatureResultMesh);
}

/**
 * 判断对象是否可作为圆角/倒角的选择输入。
 *
 * @param object 待判断对象。
 * @returns 是否为受支持的拓扑代理对象。
 */
export function isBlendSelectableObject(object: unknown): object is BlendSelectionObject {
  if (!object || typeof object !== 'object') {
    return false;
  }
  if (isObjectInsideFeaturePreviewMesh(object as FeatureFace | FeatureLine | FeaturePoint)) {
    return false;
  }
  return isFeatureFace(object as never)
    || isFeatureLine(object as never)
    || isFeaturePoint(object as never);
}

/**
 * 解析面的持久化引用。
 *
 * @param mesh 所属结果体。
 * @param faceIndex 面索引。
 * @returns 面持久化引用。
 */
function resolveFacePersistentRef(
  mesh: FeatureResultMesh,
  faceIndex: number,
): string {
  const snapshotFace = mesh.getTopologySnapshot()?.faces.find((candidate) => candidate.index === faceIndex);
  if (snapshotFace?.ref) {
    return snapshotFace.ref;
  }
  const metadata = resolveFeatureMetadata(mesh);
  const provenance = resolveTopologyProvenance(metadata);
  return provenance?.faces.find((entry) => entry.faceIndex === faceIndex)?.ref
    ?? buildTopologyFaceRef(faceIndex);
}

/**
 * 构建面的运行时拓扑提示。
 *
 * @param mesh 所属结果体。
 * @param faceIndex 面索引。
 * @param persistentRef 面持久化引用。
 * @returns 运行时拓扑提示。
 */
function buildFaceRuntimeTopologyHint(
  mesh: FeatureResultMesh,
  faceIndex: number,
  persistentRef: string,
): RuntimeTopologyHint {
  const snapshotFace = mesh.getTopologySnapshot()?.faces.find((candidate) => candidate.index === faceIndex);
  return {
    kind: 'face',
    subEntityId: snapshotFace?.subEntityId,
    topologyIndex: faceIndex,
    sourceObjectId: mesh.objectId,
    persistentRef,
  };
}

/**
 * 解析边的相邻面提示。
 *
 * @param mesh 所属结果体。
 * @param adjacentFaceIndices 相邻面索引列表。
 * @returns 面引用与面提示集合。
 */
function resolveAdjacentFacePayload(
  mesh: FeatureResultMesh,
  adjacentFaceIndices: number[],
): {
  adjacentFaceRefs: string[];
  adjacentFaceHints: RuntimeTopologyFaceHint[];
} {
  const metadata = resolveFeatureMetadata(mesh);
  const provenance = resolveTopologyProvenance(metadata);
  const adjacentFaceRefs = adjacentFaceIndices.map((faceIndex) => resolveFacePersistentRef(mesh, faceIndex));
  const adjacentFaceHints = adjacentFaceIndices.map((faceIndex) => {
    const snapshotFace = mesh.getTopologySnapshot()?.faces.find((candidate) => candidate.index === faceIndex);
    const matchedFace = provenance?.faces.find((entry) => entry.faceIndex === faceIndex);
    return {
      subEntityId: snapshotFace?.subEntityId ?? matchedFace?.subEntityId,
      topologyIndex: faceIndex,
      persistentRef: snapshotFace?.ref ?? matchedFace?.ref ?? buildTopologyFaceRef(faceIndex),
      shapeHash: undefined,
    };
  });
  return {
    adjacentFaceRefs,
    adjacentFaceHints,
  };
}

/**
 * 解析边的持久化引用。
 *
 * @param mesh 所属结果体。
 * @param edgeIndex 边索引。
 * @returns 边持久化引用；无法解析时返回 `null`。
 */
function resolveEdgePersistentRef(
  mesh: FeatureResultMesh,
  edgeIndex: number,
): string | null {
  const snapshot = mesh.getTopologySnapshot();
  const edge = snapshot?.edges.find((candidate) => candidate.index === edgeIndex);
  const vertexA = typeof edge?.vertexAIndex === 'number'
    ? snapshot?.vertices[edge.vertexAIndex]
    : null;
  const vertexB = typeof edge?.vertexBIndex === 'number'
    ? snapshot?.vertices[edge.vertexBIndex]
    : null;
  if (!edge || !vertexA || !vertexB) {
    const metadata = resolveFeatureMetadata(mesh);
    const provenance = resolveTopologyProvenance(metadata);
    const byProvenance = provenance?.edges.find((entry) => entry.edgeIndex === edgeIndex)?.ref;
    return typeof byProvenance === 'string' && byProvenance.length > 0
      ? byProvenance
      : null;
  }
  if (edge.ref) {
    return edge.ref;
  }

  /**
   * 结果体的可交互 `FeatureLine.edgeIndex` 来自视口侧的拓扑快照顺序，
   * 不能直接拿 worker 里按 Puppy CAD 内核 枚举顺序构建的 provenance 做主索引。
   * 因此这里必须优先使用结果体自身快照的端点几何生成边引用，
   * 再让 worker 通过同一套几何 ref 回绑到权威 Puppy CAD 内核 边。
   */
  return buildTopologyEdgeRef(
    toPointTuple(vertexA.position),
    toPointTuple(vertexB.position),
  );
}

/**
 * 构建边的运行时拓扑提示。
 *
 * @param mesh 所属结果体。
 * @param edgeIndex 边索引。
 * @param persistentRef 边持久化引用。
 * @param adjacentFaceIndices 相邻面索引列表。
 * @returns 运行时拓扑提示。
 */
function buildEdgeRuntimeTopologyHint(
  mesh: FeatureResultMesh,
  edgeIndex: number,
  persistentRef: string,
  adjacentFaceIndices: number[],
): RuntimeTopologyHint {
  const snapshotEdge = mesh.getTopologySnapshot()?.edges.find((candidate) => candidate.index === edgeIndex);
  const facePayload = resolveAdjacentFacePayload(mesh, adjacentFaceIndices);
  return {
    kind: 'edge',
    subEntityId: snapshotEdge?.subEntityId,
    topologyIndex: edgeIndex,
    sourceObjectId: mesh.objectId,
    persistentRef,
    adjacentFaceRefs: facePayload.adjacentFaceRefs,
    adjacentFaceHints: facePayload.adjacentFaceHints,
  };
}

/**
 * 解析顶点的持久化引用。
 *
 * @param mesh 所属结果体。
 * @param vertexIndex 顶点索引。
 * @returns 顶点持久化引用；无法解析时返回 `null`。
 */
function resolveVertexPersistentRef(
  mesh: FeatureResultMesh,
  vertexIndex: number,
): string | null {
  const vertex = mesh.getTopologySnapshot()?.vertices[vertexIndex];
  if (!vertex) {
    return null;
  }
  return vertex.ref ?? buildTopologyVertexRef(toPointTuple(vertex.position));
}

/**
 * 构建顶点的运行时拓扑提示。
 *
 * @param mesh 所属结果体。
 * @param vertexIndex 顶点索引。
 * @param persistentRef 顶点持久化引用。
 * @returns 运行时拓扑提示。
 */
function buildVertexRuntimeTopologyHint(
  mesh: FeatureResultMesh,
  vertexIndex: number,
  persistentRef: string,
): RuntimeTopologyHint {
  const snapshotVertex = mesh.getTopologySnapshot()?.vertices.find((candidate) => candidate.index === vertexIndex);
  return {
    kind: 'vertex',
    subEntityId: snapshotVertex?.subEntityId,
    topologyIndex: vertexIndex,
    sourceObjectId: mesh.objectId,
    persistentRef,
  };
}

/**
 * 将拓扑代理对象转换为选择引用。
 *
 * @param object 拓扑代理对象。
 * @returns 选择引用与显示项；无法解析时返回 `null`。
 */
export function buildBlendSelectionFromObject(
  object: BlendSelectionObject,
): BlendSelectionBuildResult | null {
  return buildBlendSelectionFromTopologyPick(resolveTopologySelectionPick(object));
}

/**
 * 将拓扑代理对象转换为帮助点引用。
 *
 * @param object 拓扑代理对象。
 * @returns 帮助点引用；当前对象不是顶点时返回 `null`。
 */
export function buildBlendPointRefFromObject(
  object: BlendSelectionObject,
): BlendPointRef | null {
  if (!(object instanceof FeaturePoint) || typeof object.vertexIndex !== 'number') {
    return null;
  }

  const mesh = resolveBlendSourceMesh(object);
  if (!mesh) {
    return null;
  }

  const snapshotVertex = mesh.getTopologySnapshot()?.vertices[object.vertexIndex];
  const point = snapshotVertex
    ? toPointTuple(snapshotVertex.position)
    : [object.position.x, object.position.y, object.position.z] as [number, number, number];
  const persistentRef = resolveVertexPersistentRef(mesh, object.vertexIndex) ?? undefined;

  return {
    bodyObjectId: mesh.objectId,
    subEntityId: object.subEntityId ?? undefined,
    persistentRef,
    point,
    runtimeTopologyHint: persistentRef
      ? buildVertexRuntimeTopologyHint(mesh, object.vertexIndex, persistentRef)
      : undefined,
    displayLabel: `顶点<${object.vertexIndex + 1}>`,
  };
}

/**
 * 将场景对象转换为部分边线参考点。
 *
 * 设计说明：
 * 1. reference-offset 在本项目中只允许点引用；
 * 2. 模型顶点复用现有拓扑点引用链路；
 * 3. 草图点使用自身坐标和草图组身份，允许用户从其它草图吸收参考点。
 *
 * @param object 场景对象。
 * @returns 参考点；非点对象返回 `null`。
 */
export function buildBlendPartialReferencePointFromObject(object: unknown): BlendPointRef | null {
  if (isBlendSelectableObject(object)) {
    return buildBlendPointRefFromObject(object);
  }
  if (!isSketchPointObject(object)) {
    return null;
  }
  return buildBlendPointRefFromSketchPoint(object);
}

/**
 * 从显示项中读取模型拓扑索引。
 *
 * @param item 显示项。
 * @returns 从零开始的模型拓扑索引；缺少有效索引时返回 `null`。
 */
function resolveDisplayItemTopologyIndex(item: BlendSelectionDisplayItem): number | null {
  const directIndex = item.kind === 'edge'
    ? item.edgeIndex
    : item.kind === 'face'
      ? item.faceIndex
      : item.kind === 'vertex'
        ? item.vertexIndex
        : undefined;
  const fallbackIndex = item.runtimeTopologyHint?.kind === item.kind
    ? item.runtimeTopologyHint.topologyIndex
    : undefined;
  const candidate = directIndex ?? fallbackIndex;
  /**
   * 拓扑快照索引是从零开始的整数；这里收紧输入，避免把不完整的运行时提示
   * 误展示成 SolidWorks 风格的实体编号。
   */
  return typeof candidate === 'number' && Number.isInteger(candidate) && candidate >= 0
    ? candidate
    : null;
}

/**
 * 按模型拓扑索引生成单个选择项的显示标签。
 *
 * @param item 显示项。
 * @returns 保留真实拓扑序号的显示项；缺少拓扑索引时回退到单项列表编号。
 */
export function labelBlendSelectionItemByTopologyIndex(
  item: BlendSelectionDisplayItem,
): BlendSelectionDisplayItem {
  const topologyIndex = resolveDisplayItemTopologyIndex(item);
  if (topologyIndex === null) {
    /**
     * 历史数据或外部导入数据可能没有拓扑索引，此时仍沿用既有列表编号规则，
     * 保证面板至少能给出稳定、可读的选择项名称。
     */
    return relabelBlendSelectionItems([item])[0] ?? item;
  }
  return {
    ...item,
    label: buildSelectionLabel(item.kind, topologyIndex + 1),
  };
}

/**
 * 重建显示标签序号。
 *
 * @param items 显示项列表。
 * @returns 重编号后的显示项列表。
 */
export function relabelBlendSelectionItems(
  items: BlendSelectionDisplayItem[],
): BlendSelectionDisplayItem[] {
  return items.reduce<{
    countersByKind: Partial<Record<BlendSelectionDisplayItem['kind'], number>>;
    labeledItems: BlendSelectionDisplayItem[];
  }>((state, item) => {
    const nextIndex = (state.countersByKind[item.kind] ?? 0) + 1;
    /**
     * SolidWorks 在同一个选择框内按对象类型独立编号：
     * 边线与面混合选择时应显示“边线<1> 面<1>”，不能共享全局序号池。
     */
    return {
      countersByKind: {
        ...state.countersByKind,
        [item.kind]: nextIndex,
      },
      labeledItems: [
        ...state.labeledItems,
        {
          ...item,
          label: buildSelectionLabel(item.kind, nextIndex),
        },
      ],
    };
  }, {
    countersByKind: {},
    labeledItems: [],
  }).labeledItems;
}
